import { UserRole } from '@depanneo/types'
import { requiredRoles } from '@/shared/utils/require-roles'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { siteKeys } from '@/hooks/sites/query-keys'
import { getSiteById } from '@/api/sites-api'
import { DetailError } from '@/components/shared/detail-error'
import {
  isMissingResourceError,
  retryUnlessRefused,
} from '@/shared/utils/network-error-utils'

export type SiteTabs =
  | 'details'
  | 'billing'
  | 'contacts'
  | 'scheduling&access'
  | 'equipments'
  | 'interventions'

const allowedTabs: SiteTabs[] = [
  'details',
  'billing',
  'contacts',
  'interventions',
  'equipments',
  'scheduling&access',
]

interface SiteSearch {
  tab: SiteTabs
  existing?: boolean
  action?: 'edit' | undefined
}

export const Route = createFileRoute(
  '/_authenticated/org/$orgId/sites/$siteId'
)({
  validateSearch: (search: Record<string, string>): SiteSearch => {
    const { tab, action, existing } = search

    const validatedTab = allowedTabs.includes(tab as SiteTabs)
      ? (tab as SiteTabs)
      : 'details'

    const validatedAction =
      validatedTab === 'details' && action === 'edit' ? 'edit' : undefined
    const validatedExisting =
      validatedTab === 'equipments' && existing ? true : undefined

    return {
      tab: validatedTab,
      action: validatedAction ?? undefined,
      existing: validatedExisting,
    }
  },
  beforeLoad() {
    requiredRoles([UserRole.ADMIN, UserRole.FACILITY_MANAGER, UserRole.VIEWER])
  },
  // Another org's site, a deleted one, a hand-edited id (#2352): the loader's
  // 404 becomes this route's not-found screen, settled without retries. Only
  // the loader's — a child's failure, or a 403 (an ability the app got wrong),
  // still reaches `ErrorFallback`, with its retry and its Sentry report.
  loader: async ({ context: { queryClient }, params: { siteId } }) => {
    try {
      return await queryClient.ensureQueryData({
        queryKey: siteKeys.detail(siteId),
        queryFn: () => getSiteById(siteId),
        retry: retryUnlessRefused,
      })
    } catch (error) {
      if (isMissingResourceError(error)) throw notFound()
      throw error
    }
  },
  notFoundComponent: () => (
    <DetailError
      error={null}
      copy={{
        refused: 'Site introuvable ou inaccessible',
        refusedDetail:
          "Il n'existe pas, a été supprimé, ou vous n'y avez pas accès.",
      }}
    />
  ),
})
